import React from 'react'
import moment from 'moment'
import {
  MenuItem,
  useTheme,
  TextField,
  Chip,
  FormControlLabel,
  IconButton
} from '@mui/material'
import { Back, CloseCircle, Trash } from 'iconsax-react'
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers'
import { useLocation, useParams } from 'react-router'
import { AnimatePresence, motion } from 'framer-motion'
import { format } from 'date-fns'
import {
  DELETED_CHANNEL,
  DaysOfWeek,
  TIME_INTERVAL_OPTIONS,
  TimeUnit,
  mapFromServer,
  mapToServer
} from './helpers'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'
import { track } from '@SonarCX/lib/mixpanel'

import BaseModal from '../ChatModals/BaseModal2'
import Button from '../../atoms/Button2'
import ButtonSecondary from '../../atoms/ButtonSecondary'
import StyledCheckbox from '@SonarCX/components/atoms/Checkbox'
import ReminderItem from '../ReminderItem'
import Switch from '@SonarCX/components/atoms/SwitchV2'
import RedButton from '@SonarCX/components/atoms/RedButton'

import { useAreYouSure } from '@SonarCX/providers/AreYouSureProvider'
import { useEnvironment } from '@SonarCX/providers/EnvironmentProvider'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import { AddErrorAlert, AddSuccessAlert } from '@SonarCX/slices/alert'
import { selectConvo } from '@SonarCX/slices/convo'
import {
  changeMutateDashboard,
  changeMutateDashboardOnEdit,
  closeReminderModal,
  createReminder,
  deleteReminder,
  selectReminder,
  updateReminder
} from '@SonarCX/slices/reminder'

import useStyles from './styles'

interface ReminderModalProps {
  data?: APIReminder
}

type RouteParams = {
  id: string
}

const handleObjectTypeName = (path: string) => {
  if (path.includes('tasks')) return 'task'
  if (path.includes('customers')) return 'user'
  if (path.includes('users')) return 'user'
  if (path.includes('documents')) return 'article'
  if (path.includes('conversations')) return 'session'
  return ''
}

const initialRepeatState = {
  by: 'day',
  every: 1,
  dow: [],
  ends_on: null,
  occurrences: 0
}

const initialState: LocalReminder = {
  id: '',
  title: '',
  details: '',
  stamp: moment().add(1, 'hour'),
  object_id: null,
  object_type: null,
  user_id: '',
  alerts: [
    {
      channel_id: 'Push',
      minutes_before: 5,
      channel_value: 'Push',
      multiplier: 'Minutes'
    }
  ],
  repeat: null
}

const ReminderModal: React.FC<ReminderModalProps> = ({ data }) => {
  const { id } = useParams<RouteParams>()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { user, loaded: envLoaded } = useEnvironment()
  const { lastSelected } = useAppSelector(selectConvo)
  const { mutateDashboardOnEdit } = useAppSelector(selectReminder)
  const [reminder, setReminder] = React.useState<LocalReminder>(
    data
      ? mapFromServer(data, user)
      : { ...initialState, stamp: moment().add(1, 'hour') }
  )
  const {
    setTitle: setCloseTitle,
    setAction: setCloseAction,
    setReason: setCloseReason,
    setSubtext,
    setV2
  } = useAreYouSure()
  const [loading, setLoading] = React.useState(false)
  const [hasRepeat, setHasRepeat] = React.useState(reminder.repeat !== null)
  const editing = React.useMemo(() => !!reminder.id, [reminder.id])
  const itemName =
    reminder.object_type ?? handleObjectTypeName(location.pathname)
  const hasConcerningItemId =
    itemName === 'session' && lastSelected ? lastSelected : id
  const { isSmall, isMedium } = useBreakpoint()
  const classes = useStyles()
  const theme = useTheme()

  const dayOfWeek = React.useMemo(
    () => format(new Date(reminder.stamp), 'EEE').toLowerCase(),
    [reminder]
  )

  const handleOnClose = React.useCallback(() => {
    dispatch(closeReminderModal())
  }, [dispatch])

  const handleAddNotification = React.useCallback(() => {
    const newNotif = {
      channel_id: 'Push',
      minutes_before: 5,
      channel_value: 'Push',
      multiplier: 'Minutes'
    }
    setReminder(old => ({
      ...old,
      alerts: [...old.alerts, newNotif]
    }))
  }, [])

  const handleReminderRowChange = React.useCallback(
    (val, index, prop) => {
      const oldValues = [...reminder.alerts]
      oldValues[index][prop] = val
      setReminder(old => ({ ...old, alerts: oldValues }))
    },
    [reminder]
  )

  const handlePillClick = React.useCallback(
    day => {
      if (reminder.repeat.dow.includes(day)) {
        setReminder(old => ({
          ...old,
          repeat: { ...old.repeat, dow: old.repeat.dow.filter(d => d !== day) }
        }))
      } else {
        setReminder(old => ({
          ...old,
          repeat: {
            ...old.repeat,
            dow:
              //only allow 1 day on repeat by month
              reminder.repeat.by === TimeUnit.month
                ? [day]
                : [...old.repeat.dow, day]
          }
        }))
      }
    },
    [reminder]
  )

  const handleCheckboxClick = React.useCallback(
    e => {
      const checked = e.target.checked
      if (checked) {
        setReminder(old => ({
          ...old,
          object_id: hasConcerningItemId,
          object_type: itemName
        }))
      } else {
        setReminder(old => ({
          ...old,
          object_id: null,
          object_type: null
        }))
      }
    },
    [hasConcerningItemId, itemName]
  )

  const handleRepeatClick = React.useCallback(() => {
    const newRepeatValue = !hasRepeat
    setHasRepeat(newRepeatValue)
    if (newRepeatValue) {
      setReminder(old => ({ ...old, repeat: initialRepeatState }))
    } else {
      setReminder(old => ({ ...old, repeat: null }))
    }
  }, [hasRepeat])

  const handleRepeatByChange = React.useCallback(e => {
    const val = e.target.value

    setReminder(old => ({
      ...old,
      repeat: {
        ...old.repeat,
        by: val,
        dow: []
      }
    }))
  }, [])

  const handleOnSaveClick = React.useCallback(async () => {
    try {
      setLoading(true)

      if (
        reminder.id &&
        reminder.alerts.some(x => x.channel_id === DELETED_CHANNEL)
      ) {
        dispatch(
          AddErrorAlert({ err: { message: 'Please select an active channel' } })
        )
        setLoading(false)
        return
      }

      const mappedReminder = mapToServer(reminder, user.id)
      if (editing) {
        track('REMINDER_UPDATED', { reminder })
        await dispatch(updateReminder(reminder.id, mappedReminder))
        if (mutateDashboardOnEdit) {
          dispatch(changeMutateDashboardOnEdit(false))
          dispatch(changeMutateDashboard(true))
        }
      } else {
        track('REMINDER_CREATED', { reminder })
        await dispatch(createReminder(mappedReminder))
      }
      dispatch(AddSuccessAlert(`Reminder ${editing ? 'updated' : 'created'}`))
      handleOnClose()
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
    } finally {
      setLoading(false)
    }
  }, [
    reminder,
    user.id,
    editing,
    dispatch,
    handleOnClose,
    mutateDashboardOnEdit
  ])

  const handleOnDeleteClick = React.useCallback(() => {
    try {
      const onConfirm = async () => {
        await dispatch(deleteReminder(reminder.id))
        dispatch(AddSuccessAlert(`Reminder deleted`))
        handleOnClose()
      }

      setV2(true)
      setCloseTitle('Delete reminder?')
      setCloseReason('Are you sure you want to delete this reminder?')
      setSubtext('')
      setCloseAction(() => onConfirm())
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
    }
  }, [
    setV2,
    setCloseTitle,
    setCloseReason,
    setSubtext,
    setCloseAction,
    dispatch,
    reminder.id,
    handleOnClose
  ])

  // add unavailable channel option
  const reminderChannelOptions = React.useMemo<ExtendedLocalAlert[]>(() => {
    if (!envLoaded) return [] as ExtendedLocalAlert[]
    const defaultVal = [{ channel_id: 'Push', channel_value: 'Push' }]

    if (
      reminder.id &&
      reminder.alerts.some(x => x.channel_id === DELETED_CHANNEL)
    ) {
      defaultVal.push({
        channel_id: DELETED_CHANNEL,
        channel_value: DELETED_CHANNEL
      })
    }

    const currentChannels = user?.channels ?? []

    const extraChannels = currentChannels
      .filter(x => x.compliance && x.validated)
      .map(c => ({ channel_id: c.id, channel_value: c.value }))
    return [...defaultVal, ...extraChannels]
  }, [envLoaded, user, reminder])

  const isValid = React.useMemo(() => {
    if (!reminder.title || !reminder.details || !reminder.stamp) return false
    return true
  }, [reminder])

  return (
    <BaseModal
      open
      showTitleOnMobile
      title={editing ? 'Edit reminder' : 'New reminder'}
      onClose={handleOnClose}
      PaperProps2={{
        style: {
          padding: '20px 16px 24px 16px',
          border: '1px solid #C6C6C6',
          borderRadius: '4px',
          width: isSmall ? '100%' : isMedium ? 'calc(100% - 4rem)' : '100%',
          maxWidth: isMedium ? 'unset' : '660px',
          height: isSmall ? 'calc(100% - 52px)' : isMedium && 'auto',
          maxHeight: isMedium && 'unset',
          minHeight: isMedium && 'auto',
          top: isSmall && '46%'
        }
      }}
      classes={{ title: classes.modalTitle, close: classes.modalClose }}
      childrenClass={classes.noOverflow}
    >
      <div className={classes.dataRoot}>
        <div className={classes.borderlessSectionRoot}>
          <TextField
            autoFocus
            placeholder='Title*'
            inputProps={{ maxLength: 150 }}
            value={reminder.title}
            onChange={e => {
              const val = e.target.value
              setReminder(old => ({ ...old, title: val }))
            }}
            fullWidth
            variant='standard'
          />
        </div>

        <div className={classes.timeSelectionSectionRoot}>
          <DateTimePicker
            //@ts-ignore - this is a bug in the library
            value={reminder.stamp}
            disablePast
            label='Reminder start date'
            timeSteps={{ minutes: 1 }}
            sx={{
              '.MuiInputBase-input': {
                fontSize: theme.size.base
              }
            }}
            slotProps={{
              // error = null is a hack to remove the red border
              textField: { variant: 'standard', error: null },
              actionBar: { actions: ['accept'] }
            }}
            onChange={date =>
              setReminder(old => ({
                ...old,
                stamp: date,
                //clear dow, since now the day is different
                repeat: old.repeat === null ? null : { ...old.repeat, dow: [] }
              }))
            }
          />
        </div>

        <div className={classes.borderlessSectionRoot}>
          <div className={classes.subheaderLaber}>Notify me</div>
          {reminder.alerts.map((n, index) => (
            <div
              key={`row-${n.channel_id}-${index}`}
              className={classes.reminderRow}
            >
              <TextField
                value={n.channel_id}
                select
                variant='standard'
                sx={{ width: '208px' }}
                onChange={e => {
                  const val = e.target.value
                  handleReminderRowChange(val, index, 'channel_id')
                }}
              >
                {reminderChannelOptions.map((option, index) => (
                  <MenuItem
                    key={`${option.channel_value}-${index}`}
                    value={option.channel_id}
                    disabled={option.channel_value === DELETED_CHANNEL}
                  >
                    {option.channel_value}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label=''
                value={n.minutes_before}
                onChange={e => {
                  const val = e.target.value
                  if (+val < 0) {
                    handleReminderRowChange(0, index, 'minutes_before')
                    return
                  }
                  handleReminderRowChange(val, index, 'minutes_before')
                }}
                type='number'
                variant='standard'
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  min: 0
                }}
                sx={{ width: 100 }}
              />
              <TextField
                value={n.multiplier}
                select
                variant='standard'
                onChange={e => {
                  const val = e.target.value
                  handleReminderRowChange(val, index, 'multiplier')
                }}
              >
                {TIME_INTERVAL_OPTIONS.map((option, index) => (
                  <MenuItem
                    key={`${option.interval}-${index}`}
                    value={option.interval}
                  >
                    {option.interval}
                  </MenuItem>
                ))}
              </TextField>
              <IconButton
                onClick={() => {
                  setReminder(old => {
                    const newAlerts = [...old.alerts]
                    newAlerts.splice(index, 1)
                    return { ...old, alerts: newAlerts }
                  })
                }}
              >
                <CloseCircle size={theme.size.sm} />
              </IconButton>
            </div>
          ))}
          {reminder.alerts.length < 5 && (
            <Button
              className={classes.addNotifBtn}
              onClick={handleAddNotification}
            >
              Add
            </Button>
          )}
        </div>

        {(hasConcerningItemId || !!reminder.object_id) && (
          <div className={classes.borderlessSectionRoot}>
            <div className={classes.includeRow}>
              <FormControlLabel
                label={`Include this ${itemName}`}
                style={{ marginLeft: '0' }}
                control={
                  <StyledCheckbox
                    checked={!!reminder.object_id}
                    onChange={handleCheckboxClick}
                  />
                }
              />
            </div>
            <AnimatePresence>
              {reminder.object_id && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <ReminderItem
                    object_id={reminder.object_id}
                    object_type={reminder.object_type}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
        <TextField
          multiline
          placeholder='Description*'
          maxRows={3}
          minRows={2}
          style={{ margin: '6px 0px' }}
          value={reminder.details}
          inputProps={{
            maxLength: 1000
          }}
          variant='standard'
          onChange={e => {
            const val = e.target.value
            setReminder(old => ({ ...old, details: val }))
          }}
        />

        <div className={classes.actionButtons}>
          <span className={classes.subheaderLaber}>Repeat</span>
          <Switch checked={hasRepeat} onChange={handleRepeatClick} />
        </div>

        <AnimatePresence>
          {hasRepeat && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className={classes.reminderRow}>
                <TextField
                  label='Every'
                  value={reminder.repeat.every}
                  onChange={e => {
                    const val = e.target.value
                    setReminder(old => ({
                      ...old,
                      repeat: { ...old.repeat, every: val }
                    }))
                  }}
                  type='number'
                  variant='standard'
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    min: 0
                  }}
                />
                <TextField
                  value={reminder.repeat.by}
                  label='By'
                  select
                  variant='standard'
                  sx={{ width: '208px' }}
                  onChange={handleRepeatByChange}
                >
                  {Object.values(TimeUnit).map((option, index) => (
                    <MenuItem key={`${option}-${index}`} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  label='Ocurrences'
                  value={reminder.repeat.occurrences}
                  onChange={e => {
                    const val = e.target.value
                    setReminder(old => ({
                      ...old,
                      repeat: { ...old.repeat, occurrences: val }
                    }))
                  }}
                  type='number'
                  variant='standard'
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    min: 0
                  }}
                />
              </div>
              <AnimatePresence>
                {(reminder.repeat.by === TimeUnit.week ||
                  reminder.repeat.by === TimeUnit.month) && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className={classes.reminderRow}
                  >
                    {Object.values(DaysOfWeek).map((option, index) => (
                      <Chip
                        key={`${option}-${index}`}
                        color={
                          reminder.repeat.dow.includes(option)
                            ? 'primary'
                            : 'secondary'
                        }
                        label={option}
                        onClick={() => handlePillClick(option)}
                        disabled={
                          reminder.repeat.by === TimeUnit.month &&
                          option !== dayOfWeek
                        }
                      />
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
              <div className={classes.reminderRow}>
                <DatePicker
                  //@ts-ignore - this is a bug in the library
                  value={reminder.repeat.ends_on}
                  minDate={reminder.stamp}
                  disablePast
                  label='Ends on'
                  sx={{
                    '.MuiInputBase-input': {
                      fontSize: theme.size.base
                    }
                  }}
                  slotProps={{
                    // error = null is a hack to remove the red border
                    textField: { variant: 'standard', error: null },
                    actionBar: { actions: ['clear', 'accept'] }
                  }}
                  onChange={date =>
                    setReminder(old => ({
                      ...old,
                      repeat: { ...old.repeat, ends_on: date }
                    }))
                  }
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className={classes.btnSection}>
        <ButtonSecondary
          className={classes.ctaButton}
          disabled={loading}
          onClick={handleOnClose}
        >
          {isSmall ? <Back /> : 'Cancel'}
        </ButtonSecondary>

        <div className={classes.actionButtons}>
          {editing && (
            <RedButton
              onClick={handleOnDeleteClick}
              className={classes.ctaButton}
              saving={loading}
            >
              {isSmall ? <Trash /> : 'Delete'}
            </RedButton>
          )}
          <Button
            loading={loading}
            onClick={handleOnSaveClick}
            className={classes.ctaButton}
            disabled={!isValid}
          >
            {editing ? 'Update' : 'Create'}
          </Button>
        </div>
      </div>
    </BaseModal>
  )
}

export default ReminderModal
